/**
 * Centralized onboarding redirection logic
 * 
 * This utility determines where a user should be redirected after login
 * or when accessing the dashboard. It consolidates all onboarding checks
 * to ensure consistency across the application.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { UserDetails } from '../api/apiService';
import { getCompanyLicenses } from '../api/apiService';
import { getDriverVehicles } from '../api/apiService';
import { getOnboardingState } from '../api/apiService';
import {
  isAuthoritativeOnboardingState,
  isPendingCompanyPayState,
  routeForOnboardingState,
  type OnboardingResumeRoute,
} from './onboardingResume';

export type OnboardingRedirectResult =
  | { shouldRedirect: true; route: OnboardingResumeRoute }
  | { shouldRedirect: false; route: null };

interface OnboardingRedirectOptions {
  userData: UserDetails | null;
  companies?: Array<{ id: number }>;
  employments?: Array<{ is_active: boolean; status?: string }>;
  hasVehicles?: boolean;
  checkLicenses?: boolean; // Whether to check licenses (can be expensive, skip if already checked)
  /**
   * WS7 (#207): driver funded by their OWN license. The employment they
   * declared is informative, so "waiting for activation" is not a state they
   * sit in — they follow the same vehicle-setup path as an active driver.
   *
   * Tri-state hydration guard: `null` means the self-pay entitlement is still
   * UNKNOWN (paywall status loading / not yet hydrated). While unknown we must
   * NOT treat the driver as "no entitlement" and bounce them to /onboarding —
   * that is the root cause of jobs 884/887. Only `true` (entitled) or `false`
   * (conclusively no entitlement) may change the redirect decision.
   */
  hasSelfPayLicense?: boolean | null;
}

/**
 * Determines if user should be redirected to owner onboarding
 * Checks if owner has ever had licenses in ANY company
 */
export async function shouldRedirectToOwnerOnboarding(
  userData: UserDetails | null,
  companies?: Array<{ id: number }>
): Promise<boolean> {
  if (!userData) {
    return false;
  }

  const isOwner = userData.groups?.includes('Owner');
  const hasOwnedCompanies = userData.owned_companies && userData.owned_companies.length > 0;

  // Only check for owners
  if (!isOwner && !hasOwnedCompanies) {
    return false;
  }

  // If no companies, owner needs onboarding
  if (!companies || companies.length === 0) {
    return true;
  }

  // Check if ANY company has ever had licenses
  try {
    const licenseChecks = await Promise.all(
      companies.map(async (company) => {
        try {
          const response = await getCompanyLicenses(company.id);
          
          if (response.ok && response.data?.summary?.ever_had_licenses === true) {
            return true;
          }
        } catch (error) {
          console.warn(`[shouldRedirectToOwnerOnboarding] Error checking licenses for company ${company.id}:`, error);
        }
        return false;
      })
    );
    const hasLicenses = licenseChecks.some(Boolean);
    return !hasLicenses; // Redirect if NO company has ever had licenses
  } catch (error) {
    console.error('[shouldRedirectToOwnerOnboarding] Error checking owner licenses across companies:', error);
    // Network errors should not force onboarding redirect — fall back to false (no redirect)
    // so the caller can decide appropriately.
    const isNetworkError = error instanceof TypeError
      || (error instanceof Error && /fetch|network|timeout|abort/i.test(error.message));
    if (isNetworkError) {
      return false;
    }
    return true; // Non-network errors: redirect to onboarding to be safe
  }
}

/**
 * Determines if user should be redirected to driver onboarding
 * Checks multiple scenarios:
 * 1. First run on device (no deviceInitialized)
 * 2. No employment at all
 * 3. Has employment but no vehicle
 */
export async function shouldRedirectToDriverOnboarding(
  userData: UserDetails | null,
  employments?: Array<{ is_active: boolean; status?: string }>,
  hasVehicles?: boolean,
  hasSelfPayLicense: boolean | null = false
): Promise<boolean> {
  if (!userData) {
    return false;
  }

  const isOwner = userData.groups?.includes('Owner');
  const hasOwnedCompanies = userData.owned_companies && userData.owned_companies.length > 0;

  // Don't redirect owners - they have their own onboarding flow
  if (isOwner || hasOwnedCompanies) {
    return false;
  }

  try {
    // SCENARIO 1: First run on this device (permissions not completed yet)
    const deviceInitialized = await AsyncStorage.getItem('deviceInitialized');
    if (!deviceInitialized) {
      return true;
    }

    // SCENARIO 2: No employment at all (never completed onboarding)
    const hasActiveEmployment = employments?.some((emp) => emp.is_active || emp.status === 'active') ?? false;
    // WS7 (#207): a self-pay driver is entitled by their own license, so they
    // follow the active-driver path even while the employment is informative.
    // Tri-state hydration guard: `null` means the self-pay entitlement is still
    // UNKNOWN (paywall status loading / not yet hydrated). While unknown we must
    // NOT treat the driver as "no entitlement" and bounce them to /onboarding —
    // that is the root cause of jobs 884/887. Only a conclusive `false` (resolved
    // no_subscription / is_self_pay=false) sends them to onboarding.
    const selfPayKnown = hasSelfPayLicense === true || hasSelfPayLicense === false;
    const canOperate = hasActiveEmployment || (selfPayKnown && hasSelfPayLicense === true);
    if (!canOperate && (!employments || employments.length === 0)) {
      // Unknown self-pay entitlement → hold on the dashboard, do not bounce.
      if (!selfPayKnown) return false;
      return true;
    }

    // SCENARIO 2B: Has employment but not active (waiting for company activation)
    // Don't redirect - let PendingWarningModal handle this
    if (!canOperate && employments && employments.length > 0) {
      return false;
    }

    // SCENARIO 3: May operate but has no vehicle
    if (canOperate && hasVehicles !== true) {
      if (hasVehicles === false) {
        return true;
      }

      // hasVehicles is undefined, check backend
      try {
        const vehiclesResponse = await getDriverVehicles();
        const hasVehiclesData = vehiclesResponse.ok && vehiclesResponse.data?.has_vehicles;
        if (!hasVehiclesData) {
          return true;
        }
      } catch (error) {
        console.error('Error checking vehicles:', error);
        // On error, don't redirect by vehicle rule; device gate is enforced above
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking driver onboarding conditions:', error);
    return true; // On error, redirect to onboarding to enforce device-permission gate
  }
}

/**
 * Main function to determine onboarding redirection
 * 
 * This function consolidates all onboarding checks and returns
 * the appropriate route to redirect to, or null if no redirect is needed.
 * 
 * @param options - Configuration options for the redirect check
 * @returns Promise with redirect result
 */
const failClosedOnboardingRedirect = (): OnboardingRedirectResult => ({
  shouldRedirect: true,
  route: '/onboarding/state-error',
});

/**
 * The onboarding state endpoint is the only redirect authority. A failed or
 * malformed response deliberately stops at the dedicated blocked route: no
 * local storage, employment, vehicle, or license signal may advance the user.
 */
async function redirectFromAuthoritativeState(
  userData: UserDetails,
  hasVehicles?: boolean,
): Promise<OnboardingRedirectResult> {
  try {
    const response = await getOnboardingState();
    if (!response.ok || !isAuthoritativeOnboardingState(response.data)) {
      return failClosedOnboardingRedirect();
    }
    if (
      response.data.persona === 'DRV-SELF'
      && response.data.current_step === 'self_pay_active'
      && hasVehicles === false
    ) {
      return { shouldRedirect: true, route: '/onboarding/vehicle' };
    }
    const hasPendingCompanyEmployment = userData.employments?.some((employment) => (
      employment.role === 'driver'
      && employment.is_active === false
      && employment.activated_at == null
      && employment.deactivated_at == null
      && employment.has_ever_been_activated !== true
    )) === true;
    if (isPendingCompanyPayState(response.data) && hasPendingCompanyEmployment) {
      let pendingDriverHasVehicle = hasVehicles;
      if (pendingDriverHasVehicle === undefined) {
        const vehiclesResponse = await getDriverVehicles();
        if (!vehiclesResponse.ok) {
          return failClosedOnboardingRedirect();
        }
        pendingDriverHasVehicle = Boolean(vehiclesResponse.data?.has_vehicles);
      }
      return {
        shouldRedirect: true,
        route: pendingDriverHasVehicle ? '/dashboard/home' : '/onboarding/vehicle',
      };
    }
    return { shouldRedirect: true, route: routeForOnboardingState(response.data) };
  } catch (error) {
    console.warn('[Onboarding Redirect] Authoritative state unavailable:', error);
    return failClosedOnboardingRedirect();
  }
}

export async function getOnboardingRedirect(
  options: OnboardingRedirectOptions
): Promise<OnboardingRedirectResult> {
  if (!options.userData) {
    return { shouldRedirect: false, route: null };
  }
  return redirectFromAuthoritativeState(options.userData, options.hasVehicles);
}

/** Login uses exactly the same server-authoritative decision as dashboard entry. */
export async function getOnboardingRedirectForLogin(
  userData: UserDetails | null
): Promise<OnboardingRedirectResult> {
  if (!userData) {
    return { shouldRedirect: false, route: null };
  }
  return redirectFromAuthoritativeState(userData);
}

